import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { deiterateAPI } from "@/js/external-js/variableModules/utils/deiterateAPI.util";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { TOUR_STEP_IDS } from "@/lib/tour-constants";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useTenancy } from "@/contexts/TenancyContext";
import { saveUserInfo } from "@/services/userService";

const DashboardRefreshButton: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [longPressActive, setLongPressActive] = useState(false);
  const { activeTenancy } = useTenancy();

  const navigate = useNavigate();
  const handleRefresh = async () => {
    // Block refresh if long-press dialog is active
    if (longPressActive) return;
    setLoading(true);
    try {
      await deiterateAPI.POST("/dashboard/refresh", { customerIds: [activeTenancy?.customerID] });
      toast.success("Dashboard refresh triggered successfully. Reloading...");
      setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 1000);
    } catch (error: any) {
      toast.error("Failed to trigger dashboard refresh. " + (error?.message || ""));
    } finally {
      setLoading(false);
    }
  };


  // Check if route is currently /dashboard
  const currentPath = window.location.pathname;
  if (currentPath !== "/dashboard") {
    return null;
  }
  
  // Long-press handlers
  // Long-press logic
  const handleMouseDown = () => {
    if (loading || resetting) return;
    const timer = setTimeout(() => {
      setShowResetDialog(true);
      setLongPressActive(true);
    }, 2000);
    setLongPressTimer(timer);
  };
  const handleMouseUp = () => {
    if (longPressTimer) clearTimeout(longPressTimer);
    setLongPressTimer(null);
  };
  const handleMouseLeave = handleMouseUp;
  const handleTouchStart = handleMouseDown;
  const handleTouchEnd = handleMouseUp;

  // When dialog closes, reset longPressActive
  const handleDialogOpenChange = (open: boolean) => {
    setShowResetDialog(open);
    if (!open) setLongPressActive(false);
  };

  // Reset dashboard config to default
  const handleConfirmReset = async () => {
    setResetting(true);
    try {
      await saveUserInfo({ dashboardConfig: [], mobileDashboardConfig: [] }); // Clear user layout, triggers default
      toast.success("Dashboard layout reset to default. Reloading...");
      setShowResetDialog(false);
      setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 1000);
    } catch (e: any) {
      toast.error("Failed to reset dashboard layout. " + (e?.message || ""));
    } finally {
      setResetting(false);
    }
  };

  return (
    <>
      <Button
        id={TOUR_STEP_IDS.REFRESH_BUTTON}
        onClick={handleRefresh}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        disabled={loading || resetting}
        aria-busy={loading || resetting}
        variant="ghost"
        className="flex items-center gap-2 text-primary"
        title="Refresh Dashboard Data (click & hold for 2s to reset layout)"
        aria-label="Refresh Dashboard Data"
      >
        {loading || resetting ? <FontAwesomeIcon icon={faRefresh} spin /> : <FontAwesomeIcon icon={faRefresh} />}
      </Button>
      <Dialog open={showResetDialog} onOpenChange={handleDialogOpenChange}>

        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle>Reset Dashboard Layout?</DialogTitle>
          </DialogHeader>
          <div>Are you sure you want to reset your dashboard layout to the default? This cannot be undone.</div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setShowResetDialog(false)} disabled={resetting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmReset} disabled={resetting}>
              {resetting ? <span className="flex items-center gap-2"><FontAwesomeIcon icon={faRefresh} spin /> Resetting...</span> : "Yes, Reset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DashboardRefreshButton;
